import {Box, Button, Grid, InputAdornment, Paper, TextField, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import {Field, Form, Formik, FormikHelpers} from "formik";
import * as Yup from "yup";
import {NavLink, useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {Add} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {BaseService} from "../../../../services/base-service";
import {URLS} from "../../../../services/app-urls";
import {translate} from "../../../../translate/translate";
import {ErrorComponent} from "../../../../components/error/error.component";
import {useKeepFilters} from "../../../../utils/form-utils";
import {Customer} from "../../../../models/general/customer";
import {useDocumentTitle} from "../../../../contexts/document-title-context";

const CustomerItemComponent = () => {
    const service = new BaseService<Customer>(URLS.CUSTOMER);
    const [object, setObject] = React.useState(new Customer());
    const [buttonValue, setButtonValue] = useState("");
    const params = useParams();
    const navigate = useNavigate();
    const url = "/general/customer/";
    const {searchParams} = useKeepFilters();
    const previousUrl = `${url}?${searchParams.toString()}`;
    const { setDocumentTitle } = useDocumentTitle();

    const schema = Yup.object().shape({
        name: Yup.string()
            .required(),
    });

    const newUrl = (value: any) => {
        const urlBase = `${url}${value}`;
        navigate(urlBase);
        window.history.replaceState(null, "", urlBase);
    };

    const handleSubmit = (values: any, {resetForm}: FormikHelpers<Customer>) => {
        service.clearParameters();
        const customer = new Customer();
        Object.assign(customer, values);
        if (customer.id) {
            try {
                service.patch(customer, customer.id).then((response) => {
                    toast.success(translate.t("successfully_updated"));
                    if (buttonValue === "save") {
                        setObject(new Customer());
                        resetForm();
                    } else {
                        setObject(response.data);
                        newUrl(response.data.id);
                        navigate(url);
                    }
                }).catch((error: any) => {
                    ErrorComponent(error);
                });
            } catch (error) {
                console.log(error);
            }
        } else {
            try {
                service.save(customer)
                    .then(response => {
                        toast.success(translate.t("successfully_registered"));
                        if (buttonValue === "save") {
                            setObject(new Customer());
                            resetForm();
                        } else {
                            setObject(response.data);
                            navigate(url);
                        }
                    })
                    .catch((error: any) => {
                        ErrorComponent(error);
                    });
            } catch (error) {
                console.log(error);
            }
        }
    };

    function onSubmit(values: any, resetForm: any) {
        service.clearParameters();
        service.getById(parseInt(params["action"] as string)).then((response) => {
            setObject(response.data);
        });
        handleSubmit(values, resetForm);
    }

    useEffect(() => {
        if (params["action"] !== "create") {
            service.getById(parseInt(params["action"] as string)).then((response) => {
                setObject(response.data);
                setDocumentTitle(response.data.name);
            });
        }
    }, []);

    return (
        <section>
            <div className="floating-title floating-title-item" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <IconButton
                    component={NavLink}
                    to={previousUrl}>
                    <ArrowBackIcon color="primary" fontSize="large" fontWeight="fontWeightBold"/>
                </IconButton>
                <Typography variant={"h5"}>
                    {translate.t("customer")}
                </Typography>
            </div>
            <div>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize
                            initialValues={object}
                            onSubmit={onSubmit}
                            validationSchema={schema}>
                            {
                                ({isValid, isValidating, errors, touched}) => (
                                    <Form autoComplete="off">
                                        <Grid container gap={2} direction={"column"} justifyContent={"space-between"}>
                                            <Grid item xs>
                                                <Field
                                                    as={TextField}
                                                    name="name"
                                                    label={translate.t("name")}
                                                    variant={"outlined"}
                                                    fullWidth
                                                    autoFocus={false}
                                                    required={true}
                                                    error={touched.name && Boolean(errors.name)}
                                                    InputProps={{
                                                        maxLength: 256,
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                {touched.name && Boolean(errors.name) && (
                                                                    <div className={"required-field"}>
                                                                        {translate.t("required_field")}
                                                                    </div>
                                                                )}
                                                            </InputAdornment>
                                                        ),
                                                    }}/>
                                            </Grid>
                                            <Grid item container spacing={2} justifyContent={"flex-end"} direction={"row"}>
                                                <Grid item>
                                                    <Button
                                                        color="primary"
                                                        component={NavLink}
                                                        to={previousUrl}
                                                        variant="outlined">
                                                        {translate.t("cancel")}
                                                    </Button>
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        color="success"
                                                        variant="contained"
                                                        type="submit"
                                                        value="save_form"
                                                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                            setButtonValue(event.currentTarget.value);
                                                        }}
                                                        name="save_form"
                                                        disabled={!isValid || isValidating}>
                                                        {translate.t("save")}
                                                    </Button>
                                                </Grid>
                                                {
                                                    !object.id &&
                                                    <Grid item>
                                                        <Button
                                                            color="primary"
                                                            variant="contained"
                                                            type="submit"
                                                            value="save"
                                                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                                setButtonValue(event.currentTarget.value);
                                                            }}
                                                            name="save"
                                                            disabled={!isValid || isValidating}>
                                                            {translate.t("save")}
                                                            <Add/>
                                                        </Button>
                                                    </Grid>
                                                }

                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </div>
        </section>
    );

};

export default CustomerItemComponent;
